/**
 * Link a person to this client — FR-B2, and the invitation of FR-B3. On the
 * kit's fields and the Branches dialog's layout since Wave 15B (C14 §10.6
 * item 7; T-6.14); what it sends is unchanged.
 *
 * The role list is the interesting part. `roleOptions` implements ADR-0015 §2
 * in the browser: **an individual entity has exactly one owner and it is the
 * individual**, so once that link exists `owner` is not offered. Both halves
 * are required (ADR-0029) — offering it and letting the API refuse teaches
 * staff the product is broken; hiding it without the API refusing is a UI-only
 * rule, which is not a rule.
 *
 * C27: the person's name must hold a letter (spec §10.19 item 1), and the two
 * required answers — who, or a new person's name — carry the red star (#19).
 * *Link* is pressable while nothing is being sent; pressing it with the answer
 * missing says so under the field (#13).
 */

import { Button, Checkbox, Dialog, Input, RadioGroup, Select } from '@fiveox/ui'
import { useEffect, useState } from 'react'

import { DialogActions, FORM_GRID, FormRow } from '../../components/page'
import { ApiError } from '../../lib/api'
import { type ClientRecord, type LinkedPerson, nameProblem } from '../../lib/api/clients'
import {
  type LinkRole,
  type PersonRecord,
  ROLE_LABELS,
  ROLE_NOTES,
  inviteOutcome,
  persons,
  roleOptions,
} from '../../lib/api/persons'

export function LinkPersonDialog({
  client,
  existing,
  onClose,
  onLinked,
}: {
  client: ClientRecord
  existing: LinkedPerson[]
  onClose: () => void
  /** Called once they are linked, with what the page should say about the
   *  invitation when it was not simply sent — not delivered, or sent without
   *  its sign-in link (C27 item 5) — said after the dialog closes. */
  onLinked: (undelivered?: string) => void
}) {
  const options = roleOptions(client.kind, existing)

  const [mode, setMode] = useState<'existing' | 'new'>('existing')
  const [people, setPeople] = useState<PersonRecord[]>([])
  const [personId, setPersonId] = useState('')
  const [fullName, setFullName] = useState('')
  const [email, setEmail] = useState('')
  const [mobile, setMobile] = useState('')
  const [role, setRole] = useState<LinkRole>(options[0])
  const [invite, setInvite] = useState(true)
  const [attempted, setAttempted] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    persons
      .list({ status: 'active', limit: 200 })
      .then((body) =>
        // Someone already on this entity is not a candidate: linking them again
        // is a conflict, and offering the option is offering a refusal.
        setPeople(
          body.data.filter((row) => !existing.some((link) => link.person_id === row.id)),
        ),
      )
      .catch(() => setPeople([]))
  }, [existing])

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    setAttempted(true)
    if (!ready) return
    setSaving(true)
    setError(null)
    try {
      const id =
        mode === 'existing'
          ? personId
          : (
              await persons.create({
                full_name: fullName.trim(),
                email: email.trim() || undefined,
                mobile: mobile.trim() || undefined,
              })
            ).data.id

      let undelivered: string | undefined
      if (invite) {
        // Inviting links as a side effect — the firm's authority *is* the
        // record (FR-B3) — so this is one call, not two.
        const result = await persons.invite(id, { client_id: client.id, role })
        // **Linked, and possibly not delivered — both are true.** Until Wave
        // 15B this reason was set on the dialog and the page closed the dialog
        // in the same breath, so nobody ever read it; it now goes to the page,
        // which says it once the dialog has closed.
        const outcome = inviteOutcome(result.data)
        if (outcome.tone !== 'success') undelivered = outcome.text
      } else {
        await persons.link(id, { client_id: client.id, role })
      }
      onLinked(undelivered)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That person could not be linked.')
    } finally {
      setSaving(false)
    }
  }

  const nameError = fullName.trim() ? nameProblem(fullName) : 'Give their name.'
  const ready = mode === 'existing' ? personId !== '' : nameError === null

  return (
    <Dialog open onClose={onClose} title={`Who manages ${client.legal_name}?`} error={error} width="lg">
      <form onSubmit={submit} noValidate>
        <RadioGroup
          legend="A person acts for this client. They are not a client themselves."
          className="mb-5 sm:flex-row sm:gap-6"
          value={mode}
          onChange={setMode}
          options={[
            { value: 'existing', label: 'Someone on your list' },
            { value: 'new', label: 'Someone new' },
          ]}
        />

        <div className={FORM_GRID}>
          {mode === 'existing' ? (
            <FormRow label="Person" htmlFor="link-person" required>
              <Select
                id="link-person"
                value={personId}
                onChange={(e) => setPersonId(e.target.value)}
                required
                aria-required="true"
                error={attempted && !personId ? 'Choose who to link.' : null}
                hint={
                  people.length === 0
                    ? 'Nobody else is on your list yet. Choose Someone new to add them.'
                    : undefined
                }
              >
                {/* A placeholder for a required answer, not a choice (#13). */}
                <option value="" disabled hidden>
                  Choose…
                </option>
                {people.map((person) => (
                  <option key={person.id} value={person.id}>
                    {person.full_name} — {person.email ?? person.mobile}
                  </option>
                ))}
              </Select>
            </FormRow>
          ) : (
            <>
              <FormRow label="Name" htmlFor="link-name" required>
                <Input
                  id="link-name"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  required
                  aria-required="true"
                  // A blank name waits for *Link*; a letterless one is said as typed.
                  error={fullName.trim() || attempted ? nameError : null}
                />
              </FormRow>
              <FormRow label="Email" htmlFor="link-email">
                <Input
                  id="link-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </FormRow>
              <FormRow label="Mobile" htmlFor="link-mobile">
                <Input
                  id="link-mobile"
                  value={mobile}
                  onChange={(e) => setMobile(e.target.value)}
                  hint="An email address or a mobile number — at least one, so they can be reached."
                />
              </FormRow>
            </>
          )}

          <FormRow label="Role">
            <RadioGroup
              legend="Role"
              hideLegend
              className="sm:pt-3"
              value={role}
              onChange={setRole}
              options={options.map((value) => ({
                value,
                label: ROLE_LABELS[value],
                description: ROLE_NOTES[value],
              }))}
            />
            {client.kind === 'individual' && !options.includes('owner') && (
              <p className="text-muted-foreground mt-2 text-xs">
                This client is an individual, and they are already their own owner. Anyone else
                acts for them.
              </p>
            )}
          </FormRow>

          <div className="sm:col-start-2">
            <Checkbox
              checked={invite}
              onChange={(e) => setInvite(e.target.checked)}
              label="Send them an invitation to your client portal"
            />
          </div>
        </div>

        <DialogActions>
          <Button type="button" variant="ghost" onClick={onClose} disabled={saving}>
            Close
          </Button>
          <Button type="submit" loading={saving} loadingText="Saving…">
            Link
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
